import { useEffect, useMemo, useRef, useState } from 'react';
import type { LayerSpecification } from 'mapbox-gl';
import {
  UMAppShell, UMBuildingDetailPanel, UMFiltersPanel, UMGeometriesPanel, UMImportPanel, UMMap,
  UMMapControls, UMMeasuresPanel, UMMessage, UMR, UM_DEMO_BUILDING, UM_LAYER_CATALOG, UM_RAIL_ITEMS,
  type UMBasemapId, type UMBuildingDetail, type UMFilterValues, type UMMapHandle, type UMRailItemId,
} from '../_components';
import { ExportPanelGeojson } from './ExportPanelGeojson';
import { useExportData } from './hooks';
import { MOCK_PERIMETERS } from './mocks';
import { SelectionPanel } from './SelectionPanel';
import { SelectionFormPanel } from './SelectionFormPanel';
import { SelectionToolbar } from './SelectionToolbar';
import { SelectionToolRail } from './SelectionToolRail';
import { useSelection } from './useSelection';
import type { ExportPerimeter, ExportResult, SelectionDraft } from './types';
import { useNetwork } from './useNetwork';
import { NETWORK_LAYERS, networkSource, useNetworkMap } from './NetworkMap';
import type { Coordinate } from './network';
import { useBuildingUpload } from './BuildingUpload';
import { SelectionLayersPanel } from './SelectionLayersPanel';
import { DensityLegendPanel } from './DensityLegendPanel';
import { MapPopup } from './PipePopup';
import { PipePopupContent } from './PipePopupContent';

export const meta = {
  title: 'Nouveau panel sélection V2',
  description: 'Reproduction du panneau actuel Contenu de la sélection : résumé Demo 2, bâtiments et tracé de réseau.',
  tags: ['map', 'network', 'poc'],
  author: 'kr4259',
  updatedAt: '2026-11-03T16:32:49+01:00',
  status: 'draft' as const,
};

// Chrome de référence conservé ; Sélection ouverte, Export conservé.

const SEVRES = MOCK_PERIMETERS[0];

/** Bâtiments fictifs dans Sèvres. heat = MWhu/an. */
const DEMO_BUILDINGS: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: Array.from({ length: 24 }, (_, i) => {
    const col = i % 6;
    const row = Math.floor(i / 6);
    const x = 2.2015 + col * 0.0028;
    const y = 48.8165 + row * 0.0034;
    const heat = i === 0 ? 420 : i === 1 ? 488 : [180, 420, 760, 1350, 95, 610][(i * 7) % 6] + i * 11;
    return {
      type: 'Feature' as const,
      id: i + 1,
      properties: { id: `BFR92072${String(i + 1).padStart(4, '0')}`, heat },
      geometry: { type: 'Polygon' as const, coordinates: [[[x, y], [x + 0.0011, y], [x + 0.0011, y + 0.0008], [x, y + 0.0008], [x, y]]] },
    };
  }),
};

function boundsOf(p: ExportPerimeter): [[number, number], [number, number]] {
  const ring = p.contour.coordinates[0];
  const lngs = ring.map((c) => c[0]);
  const lats = ring.map((c) => c[1]);
  return [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]];
}

function buildingFromFeature(id: string, heat: number): UMBuildingDetail {
  const heating = Math.round(heat * 0.8 * 10) / 10;
  return {
    ...UM_DEMO_BUILDING,
    functionalId: id,
    address: `${Number(id.slice(-2)) * 3} Grande Rue 92310 Sèvres`,
    selections: [],
    heating: { ...UM_DEMO_BUILDING.heating, consumption: heating },
    hotWater: { ...UM_DEMO_BUILDING.hotWater, consumption: Math.round((heat - heating) * 10) / 10 },
  };
}

function summary(r: ExportResult): string {
  const extra = r.layerCount - 1;
  const layers = r.format === 'geojson' ? ` — ${r.layerCount} couche${r.layerCount > 1 ? 's' : ''} (Bâtiments${extra ? ` + ${extra} autre${extra > 1 ? 's' : ''}` : ''})` : '';
  return `Export ${r.format === 'geojson' ? 'GeoJSON' : 'Excel'} lancé pour « ${r.perimeterLabel} »${layers}. Fichier ${r.fileName} disponible dans l'historique des exports (mock).`;
}

export default function NouveauPanelSelectionPrototype() {
  const mapRef = useRef<UMMapHandle>(null);
  const [panel, setPanel] = useState<UMRailItemId | null>('selection');
  const [search, setSearch] = useState('');
  const [basemap, setBasemap] = useState<UMBasemapId>('light');
  const [is3d, setIs3d] = useState(false);
  const [mode, setMode] = useState<'mapbox' | 'offline'>('offline');
  const [checked, setChecked] = useState<Record<string, boolean>>(() => Object.fromEntries(UM_LAYER_CATALOG.map((g) => [g.id, !!g.defaultChecked])));
  const [filters, setFilters] = useState<UMFilterValues>({});
  const [tool, setTool] = useState<string | null>(null);
  const [perimeter, setPerimeter] = useState<ExportPerimeter | null>(SEVRES);
  const [buildingId, setBuildingId] = useState<string | null>(null);
  const [pipePick, setPipePick] = useState<{ id: string; at: Coordinate } | null>(null);
  const [editingLegend, setEditingLegend] = useState(false);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<{ title?: string; text: string } | null>(null);
  const exportData = useExportData();

  const heatById = useMemo(() => Object.fromEntries(DEMO_BUILDINGS.features.map((f) => [f.properties!.id as string, f.properties!.heat as number])), []);
  const selection = useSelection(heatById);
  const upload = useBuildingUpload();
  const targets = useMemo<Coordinate[]>(() => DEMO_BUILDINGS.features
    .filter((f) => selection.members.includes(String(f.properties?.id)))
    .map((f) => {
      const ring = (f.geometry as GeoJSON.Polygon).coordinates[0];
      return [(ring[0][0] + ring[2][0]) / 2, (ring[0][1] + ring[2][1]) / 2];
    }), [selection.members]);
  const heatByTarget = DEMO_BUILDINGS.features.filter((f) => selection.members.includes(String(f.properties?.id)))
    .map((f) => Number(f.properties?.heat));
  const network = useNetwork(selection.selectionId ?? '', targets, selection.summary.heatTotal, [...selection.members].sort().join(','), heatByTarget);
  const editing = !!selection.activeTool || !!network.activeTool;
  useNetworkMap(mapRef, network, { blocked: !!selection.activeTool || !!tool,
    onDragStart: () => { setPanel('selection'); setBuildingId(null); },
    onPipeClick: (id, at) => { if (!editing && network.network.pipes.some((p) => p.id === id)) setPipePick({ id, at }); } });
  useEffect(() => { if (editing) setPipePick(null); }, [editing]);
  // UMRail n'expose pas de pastille : on marque le bouton « Sélection » pour la CSS du prototype.
  useEffect(() => {
    document.querySelectorAll<HTMLButtonElement>('nav[aria-label="Navigation UrbanMap"] button').forEach((b) => {
      if (b.textContent?.trim().endsWith('Sélection')) b.toggleAttribute('data-selection-active', !!selection.selectionId);
    });
  }, [selection.selectionId, panel]);
  // La colonne d'outils est en face du bouton « Sélection » du rail, panneau ouvert ou replié.
  const [toolTop, setToolTop] = useState<number | undefined>();
  useEffect(() => {
    if (!selection.selectionId) { setToolTop(undefined); return; }
    const sync = () => {
      const button = document.querySelector('nav[aria-label="Navigation UrbanMap"] button[data-selection-active]');
      const main = document.querySelector('[data-um-shell] main');
      if (!button || !main) return;
      const top = Math.round(button.getBoundingClientRect().top - main.getBoundingClientRect().top);
      setToolTop((prev) => (prev === top ? prev : top));
    };
    sync();
    const timer = window.setInterval(sync, 150);
    return () => window.clearInterval(timer);
  }, [selection.selectionId]);
  const cancelAll = () => { selection.cancel(); network.cancel(); };
  const perimeterFc = useMemo<GeoJSON.FeatureCollection>(
    () => ({ type: 'FeatureCollection', features: perimeter ? [{ type: 'Feature', properties: { id: perimeter.id }, geometry: perimeter.contour }] : [] }),
    [perimeter],
  );

  const layers = useMemo<LayerSpecification[]>(
    () => [
      { id: 'export-perimeter-fill', type: 'fill', source: 'perimeter', paint: { 'fill-color': UMR.blue, 'fill-opacity': 0.08 } },
      { id: 'export-perimeter-line', type: 'line', source: 'perimeter', paint: { 'line-color': UMR.blue, 'line-width': 2, 'line-dasharray': [2, 1] } },
      {
        id: 'urbanmap-building-fill', type: 'fill', source: 'buildings',
        paint: {
          'fill-color': ['step', ['get', 'heat'], UMR.map.consumption[0], 300, UMR.map.consumption[1], 600, UMR.map.consumption[2], 1000, UMR.map.consumption[3]],
          'fill-opacity': 0.8,
        },
      },
      { id: 'urbanmap-building-picked', type: 'line', source: 'buildings', filter: ['==', ['get', 'id'], buildingId ?? ''], paint: { 'line-color': UMR.map.picked, 'line-width': 3 } },
      { id: 'selection-building-members', type: 'line', source: 'buildings',
        filter: ['in', ['get', 'id'], ['literal', selection.members]],
        paint: { 'line-color': UMR.map.pipe, 'line-width': 3 } },
      { id: 'selection-building-pending', type: 'line', source: 'buildings',
        filter: ['in', ['get', 'id'], ['literal', selection.pending]],
        paint: { 'line-color': UMR.map.picked, 'line-width': 4 } },
      ...NETWORK_LAYERS,
    ],
    [checked, buildingId, selection.members, selection.pending],
  );

  const toggle = (id: UMRailItemId) => {
    cancelAll();
    setTool(null);
    setEditingLegend(false);
    setCreating(false);
    setPanel((current) => (current === id || ['admin', 'contact', 'logout'].includes(id) ? null : id));
  };
  const close = () => { setPanel(null); setTool(null); setEditingLegend(false); setCreating(false); cancelAll(); };
  const flash = (text: string, title?: string) => { setNotice({ text, title }); window.setTimeout(() => setNotice(null), 6000); };
  // Quitter / créer remettent la carte à plat : outils, éditeur de légende, pop-in et fiche fermés.
  const resetSelectionView = () => { cancelAll(); setEditingLegend(false); setPipePick(null); setBuildingId(null); };
  const createSelection = (draft: SelectionDraft) => {
    resetSelectionView(); selection.create(draft); setCreating(false);
    flash(`Sélection « ${draft.name} » créée localement — aucune écriture réelle.`);
  };
  const changePerimeter = (p: ExportPerimeter | null) => {
    setPerimeter(p);
    if (p) mapRef.current?.fitBounds(boundsOf(p), 64);
  };
  const building = buildingId ? buildingFromFeature(buildingId, heatById[buildingId]) : null;
  // Compteur réservé à l'ajout / retrait de bâtiments : il compte ceux en cours de désignation.
  const pickedCount = selection.activeTool ? selection.pending.length : null;
  const pipe = network.pipesVisible ? network.network.pipes.find((p) => p.id === pipePick?.id) : undefined;
  const editLegend = () => { if (!editing && !network.loading) setEditingLegend(true); };
  const pickTool = (t: string) => setTool((c) => (c === t ? null : t));

  return (
    <UMAppShell
      title="Nouveau panel sélection V2"
      railItems={UM_RAIL_ITEMS.map((it) => (it.id === 'selection' ? { ...it, disabled: false } : it))}
      activeRailId={panel}
      onRailSelect={toggle}
      toolbar={
        <SelectionToolbar
          selectionLabel={selection.selectionLabel}
          search={search}
          onSearchChange={(value) => { setSearch(value); if (value.trim()) flash('Prototype local — recherche métier non simulée.'); }}
          onMessage={flash}
          onQuit={() => { resetSelectionView(); selection.quit(); }}
          onCenter={() => mapRef.current?.fitBounds(boundsOf(SEVRES), 64)}
          activeTool={selection.activeTool}
          pendingCount={selection.pending.length} onCancel={selection.cancel}
          onValidate={() => { selection.validate(); flash('Sélection mise à jour localement — aucune écriture réelle.'); }}
        />
      }
      leftPanel={
        (editingLegend && (panel === 'selection' || panel === 'layers') && <DensityLegendPanel
          legend={network.density.legend} onSave={network.density.saveLegend} onClose={() => setEditingLegend(false)} />) ||
        (panel === 'selection' && creating && <SelectionFormPanel onClose={close} onCancel={() => setCreating(false)}
          onSubmit={createSelection} />) ||
        (panel === 'selection' && <SelectionPanel onClose={close} upload={upload} onEditLegend={editLegend}
          selections={selection.selections} selectionId={selection.selectionId} selectionLabel={selection.selectionLabel} consumption={selection.current?.consumption ?? 'Chaud'}
          onSelectionChange={(id) => {
            if (network.activeTool || network.loading) return;
            network.cancel(); setBuildingId(null); setPipePick(null); selection.changeSelection(id);
          }}
          onSelectionActions={flash} onQuit={() => { resetSelectionView(); selection.quit(); }}
          onCreate={() => { resetSelectionView(); setCreating(true); }}
          summary={{ ...selection.summary, pipes: network.length }} activeTool={selection.activeTool} network={network}
        />) ||
        (panel === 'layers' && <SelectionLayersPanel network={network} selectionLabel={selection.selectionLabel} onEditLegend={editLegend}
          onClose={close} checked={checked} onCheckedChange={(id, value) => setChecked((prev) => ({ ...prev, [id]: value }))} />) ||
        (panel === 'geometries' && <UMGeometriesPanel onClose={close} activeTool={tool} onToolChange={pickTool} hasBuilding={!!buildingId} />) ||
        (panel === 'measures' && <UMMeasuresPanel onClose={close} activeTool={tool} onToolChange={pickTool} />) ||
        (panel === 'filters' && <UMFiltersPanel onClose={close} values={filters} onChange={setFilters} />) ||
        (panel === 'export' && (
          <ExportPanelGeojson
            onClose={close}
            onHistory={() => flash('Historique des exports (mock)')}
            perimeter={perimeter}
            onPerimeterChange={changePerimeter}
            pending={exportData.isPending}
            error={exportData.error?.message ?? null}
            onExport={(req) => exportData.mutate(req, { onSuccess: (r) => flash(summary(r), 'Export lancé') })}
          />
        )) ||
        (panel === 'import' && <UMImportPanel onClose={close} onImport={(f) => flash(`Import de ${f.name} lancé (mock)`)} onHistory={() => flash('Historique des imports (mock)')} onDownloadTemplate={() => flash('Téléchargement du modèle (mock)')} />) ||
        undefined
      }
      rightPanel={
        (building && (
          <UMBuildingDetailPanel
            building={building}
            onClose={() => setBuildingId(null)}
            onEdit={() => flash('Édition du bâtiment (mock)')}
            onExpand={() => flash('Vue détaillée du bâtiment (mock)')}
            availableSelections={['Prospection IDF', 'Hauts-de-Seine Ouest']}
            onSelectionsChange={() => flash('Sélections mises à jour (mock)')}
            onStreetView={() => flash('Street View (mock)')}
            onEarth={() => flash('Google Earth (mock)')}
          />
        )) || undefined
      }
    >
      <UMMap
        ref={mapRef}
        basemap={basemap}
        is3d={is3d}
        initialBounds={boundsOf(SEVRES)}
        sources={{ perimeter: perimeterFc, 'selection-network': networkSource(network, pipe?.id), buildings: DEMO_BUILDINGS }}
        layers={layers}
        // Pipes are clickable outside edit tools only, so they never steal a building pick.
        interactiveLayerIds={editing ? ['urbanmap-building-fill'] : ['selection-network-lines', 'urbanmap-building-fill']}
        onFeatureClick={(layerId, f) => {
          // Pipe clicks are handled by useNetworkMap, which knows the clicked position.
          if (network.activeTool || network.loading || layerId === 'selection-network-lines') return;
          const id = String(f.properties?.id);
          if (selection.activeTool) { selection.pickBuilding(id); return; }
          setBuildingId(id); setPipePick(null);
        }}
        onMapClick={() => setPipePick(null)}
        onModeChange={setMode}
      />
      {pipe && pipePick && <MapPopup mapRef={mapRef} at={pipePick.at}>
        <PipePopupContent pipe={pipe} result={network.density.result} stale={network.density.stale}
          onClose={() => setPipePick(null)} />
      </MapPopup>}
      <UMMapControls
        basemap={basemap}
        availableBasemaps={mode === 'mapbox' ? ['light', 'streets', 'satellite', 'outdoors'] : ['light']}
        onBasemapChange={setBasemap}
        onZoomIn={() => mapRef.current?.zoomIn()}
        onZoomOut={() => mapRef.current?.zoomOut()}
        is3d={is3d}
        onToggle3d={setIs3d}
      />
      {selection.selectionId && <SelectionToolRail activeTool={selection.activeTool} pendingCount={selection.pending.length} top={toolTop}
        blocked={!!network.activeTool || network.loading}
        onToolChange={(id) => {
          if (network.activeTool || network.loading) return;
          setTool(null);
          selection.selectTool(id);
        }} />}
      {(selection.instruction || network.instruction) && (
        // Bandeau pleine largeur de la carte, espacé du bandeau d'outils, rétréci si la fiche bâtiment est ouverte.
        <div style={{
          position: 'absolute', top: UMR.size.toolbarHeight + 16, left: 16,
          right: (building ? UMR.size.panelWidth : 0) + 16, zIndex: 30,
        }}>
          <div role="status" aria-live="polite">
            <UMMessage title={pickedCount === null ? undefined
              : `${pickedCount} bâtiment${pickedCount > 1 ? 's' : ''} sélectionné${pickedCount > 1 ? 's' : ''}`}>
              {network.instruction || selection.instruction}
            </UMMessage>
          </div>
        </div>
      )}
      {notice && (
        <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 30, maxWidth: 520 }}>
          <UMMessage title={notice.title}>{notice.text}</UMMessage>
        </div>
      )}
    </UMAppShell>
  );
}
